import * as Actions from '../actions/flatFeeActions';
import FlatFee from '../../entities/flatFee';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type FlatFeeAction = { 
    type: string; 
    flatFee: FlatFee | null; 
    flatFeeId: string | FlatFee; 
    partial: boolean; 
    lastFetch: number; 
    flatFees: FlatFee[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<FlatFee> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {FlatFee[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<FlatFee> | null, firmId: string, userId: string, updates?: FlatFee[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'flatFee', 'flatFees', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<FlatFee>} data
 * @param {FlatFee[]} [updates]
 * @returns {ArrayMap<FlatFee>}
 */
function updateStorage(data: ArrayMap<FlatFee>, updates?: FlatFee[]): ArrayMap<FlatFee> {
	if (logging) console.debug('⮂ flatFeeReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {FlatFee[]} [updates]
 * @returns {ArrayMap<FlatFee>}
 */
function updateStorageNoReturn(updates?: FlatFee[]): void {
	if (logging) console.debug('⮂ flatFeeReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {FlatFee[]} [data]
 * @returns {ArrayMap<FlatFee>}
 */
function createState(data?: FlatFee[] | ArrayMap<FlatFee>): ArrayMap<FlatFee> {
	if (!data) return ArrayMapRegistry.clone<FlatFee>('flatFee', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<FlatFee>('flatFee', data.map(e => e));
    else return ArrayMapRegistry.clone<FlatFee>('flatFee', data);
}

function flatFees(state: ArrayMap<FlatFee>, action: FlatFeeAction): ArrayMap<FlatFee> {
	if (!state) state = createState();

	let st: ArrayMap<FlatFee>;

	switch (action.type) {
		// action.flatFee - the new value of flatFee with id action.id
		// action.flatFeeId - id of the target, used if new value is null
		case Actions.FLATFEE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ flatFeeReducer: FETCH_ONE_SUCCESS', action);

			if (action.flatFee === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.flatFeeId)) {
				st = state.clone();
				st.set(action.flatFee);
				// state.clear();

				updateStorageNoReturn([action.flatFee]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.flatFees
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.flatFees
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.flatFees
			 !partial, !stateEmpty, fetchEmpty	-> D return flatFees in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge flatFees in state newer than those in action.flatFees
		 */
		case Actions.FLATFEE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.flatFees.length === 0;
			const fetched = action.flatFees.map(flatFee => new FlatFee({...flatFee, persisted: true}));

			if (logging) console.debug(`⮂ flatFeeReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} flatFees with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ flatFeeReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ flatFeeReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<FlatFee> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: FlatFee[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(flatFee => {
					const current = st.get(flatFee.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && flatFee.status === 'D') {
							// Remove it from state
							toUpdate.push(flatFee);
							st.delete(flatFee);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (flatFee.addedOn > current.addedOn) {
								toUpdate.push(flatFee);
								st.set(flatFee);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (flatFee.status !== 'D') {
							toUpdate.push(flatFee);
							st.set(flatFee);
						}
						else if (logging) console.debug('⮂ flatFeeReducer: partialMerge: skipping deleted flatFee not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ flatFeeReducer: empty: flatFees', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ flatFeeReducer: fullMerge: flatFees', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localFlatFee => {
                    // If this flatFee has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localFlatFee.persisted) {
                        const fetchedFlatFee = st.get(localFlatFee.id);
                        if (!fetchedFlatFee || localFlatFee.addedOn > fetchedFlatFee.addedOn) st.set(localFlatFee);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.FLATFEE_FETCH_ONE_FAILURE:
			console.error('FLATFEE_FETCH_ONE_FAILURE', action.flatFeeId, action.err);

			// No update to storage required
			return state;

		case Actions.FLATFEE_FETCH_FAILURE:
			console.error('FLATFEE_FETCH_FAILURE', action.flatFeeId, action.err);

			// No update to storage required
			return state;

		case Actions.FLATFEE_RESTORE: {
		    let flatFee = new FlatFee({...action.flatFee});
            st = state.clone();

            const existing = st.get(flatFee.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (flatFee.addedOn >= existing.addedOn) {
                    st.set(flatFee);
                    return updateStorage(st, [flatFee]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (flatFee.status === 'D') return state;

                st.set(flatFee);
                return updateStorage(st, [flatFee]);
            }
        }
            
		case Actions.FLATFEE_SAVE: {
		    // console.debug('⮂ FLATFEE_SAVE reducer', action.flatFee?.id);
		    
			// clone the flatFee
			let flatFee = new FlatFee({...action.flatFee});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new flatFee in the state
			// If this is a create, add to the queue
			const _flatFee = st.get(flatFee.id);

			if (!_flatFee) st.set(flatFee);
			else {
				if (flatFee.addedOn >= _flatFee.addedOn) st.set(flatFee);
			}

			return updateStorage(st, [flatFee]);
		}

		case Actions.FLATFEE_SAVE_MANY: {
		    if (logging) console.debug('⮂ FLATFEE_SAVE_MANY reducer', action.flatFees.map(flatFee => flatFee.id).join(','));
		    
			// clone the flatFees
			let toProcess = action.flatFees.map(flatFee => new FlatFee(flatFee));

			st = state.clone();
			// state.clear();

			const updates: FlatFee[] = [];

			toProcess.forEach(flatFee => {
				// Add or update the new flatFee in the state
				// If this is a create, add to the queue
				const existing = st.get(flatFee.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (flatFee.status !== 'D') {
						st.push(flatFee);
						updates.push(flatFee);
					}
					else if (logging) console.debug('⮂ FLATFEE_SAVE_MANY: skipping deleted flatFee not in state');
				}
				else {
					if (flatFee.addedOn >= existing.addedOn) {
						st.set(flatFee);
						updates.push(flatFee);
					}
					else if (logging) console.debug('⮂ Skipping, existing flatFee had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.FLATFEE_REMOVE: {
			// clone the flatFee
			const flatFee = new FlatFee({...action.flatFee});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(flatFee);

			return updateStorage(st, [flatFee]);
		}

		case Actions.FLATFEE_RESTORE_FAILURE:
		case Actions.FLATFEE_SAVE_FAILURE: {
		    console.warn('FLATFEE_SAVE_FAILURE reducer', action.flatFee?.id);
		    if (!action.flatFee?.id) {
				console.warn('FLATFEE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const flatFee = ensure(FlatFee, action.flatFee);
			if (!flatFee) {
				console.error('Could not find flatFee after call to save');
				return state;
			}

			st = state.clone();

			const _flatFeeRef = state.get(flatFee.id);

			if (_flatFeeRef && (_flatFeeRef.addedOn === flatFee.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _flatFee = new FlatFee({..._flatFeeRef});
				_flatFee.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_flatFee.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('flatFee', _flatFee).then(r => {
						if (r.isErr()) console.error(`⮂ flatFeeReducer: failed to save flatFee with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ flatFeeReducer: saved flatFee with fatalError info');
					}).catch(err => console.error(`⮂ flatFeeReducer: failed to save flatFee with fatalError info: ${err.message}`));

				st.set(_flatFee);
			}

			return updateStorage(st);
		}

		case Actions.FLATFEE_SAVE_MANY_FAILURE: {
		    console.warn('FLATFEE_SAVE_MANY_FAILURE reducer', action.flatFees.map(flatFee => flatFee.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.FLATFEE_RESTORE_SUCCESS:
		case Actions.FLATFEE_SAVE_SUCCESS: {
			if (!action.flatFee?.id) {
				console.warn('FLATFEE_SAVE_SUCCESS reducer');
                return state;
			}

			const flatFeeRef = state.get(action.flatFee?.id);
			if (!flatFeeRef) {
				console.warn('Could not find flatFee after call to save');
                return state;
			}

			st = state.clone();

			if (action.flatFee.addedOn >= flatFeeRef.addedOn) {
				st.set(action.flatFee);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const flatFee = new FlatFee({...flatFeeRef, persisted: true});
				st.set(flatFee);
			}

			return updateStorage(st, [action.flatFee]);
		}

		case Actions.FLATFEE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ FLATFEE_SAVE_MANY_SUCCESS reducer', action.flatFees.map(flatFee => flatFee.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const flatFees: FlatFee[] = [];

			action.flatFees.forEach(e => {
				const flatFeeRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!flatFeeRef) console.warn('Could not find flatFee after call to save');
				else if (e.addedOn >= flatFeeRef.addedOn) {
				    st.set(e);
				    flatFees.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const flatFee = new FlatFee({...flatFeeRef, persisted: true});
					st.set(flatFee);
					flatFees.push(flatFee);
				}
			});

			return updateStorage(st, flatFees);
		}

		case Actions.FLATFEE_REMOVE_FAILURE: {
		    console.warn('FLATFEE_REMOVE_FAILURE action', action.flatFee?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.FLATFEE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.FLATFEE_RESTORE_MANY: {
			if (logging) console.debug('⮂ FLATFEE_RESTORE_MANY reducer', action.flatFees.map(flatFee => flatFee.id).join(','));

			const toProcess = action.flatFees.map(flatFee => new FlatFee({...flatFee}));
			st = state.clone();

			const updates: FlatFee[] = [];

			toProcess.forEach(flatFee => {
				const existing = st.get(flatFee.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (flatFee.addedOn >= existing.addedOn) {
						st.set(flatFee);
						updates.push(flatFee);
					}
					else if (logging) console.debug('⮂ FLATFEE_RESTORE_MANY: skipping older version of flatFee');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (flatFee.status !== 'D') {
						st.set(flatFee);
						updates.push(flatFee);
					}
					else if (logging) console.debug('⮂ FLATFEE_RESTORE_MANY: skipping deleted flatFee not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.FLATFEE_REMOVE_MANY: {
			if (logging) console.debug('⮂ FLATFEE_REMOVE_MANY reducer', action.flatFees.map(flatFee => flatFee.id).join(','));

			const toProcess = action.flatFees.map(flatFee => new FlatFee({...flatFee}));
			st = state.clone();

			toProcess.forEach(flatFee => {
				st.delete(flatFee);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default flatFees;